import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { UserCheck, UserX, Clock, Shield } from 'lucide-react';
import { filesApi } from '../lib/api';
import { useToast } from '../hooks/use-toast';

const FileAccessManager = ({ file, open, onClose }) => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const { toast } = useToast();

    useEffect(() => {
        if (open && file) {
            fetchRequests();
        }
    }, [open, file]);

    const fetchRequests = async () => {
        setLoading(true);
        try {
            const data = await filesApi.getAccessRequests(file.id);
            setRequests(data);
        } catch (error) {
            console.error('Failed to fetch requests:', error);
            toast({
                title: 'Error',
                description: 'Failed to load access requests',
                variant: 'destructive'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleProcess = async (requestId, action) => {
        try {
            await filesApi.processAccessRequest(requestId, action);
            setRequests(prev => prev.map(r =>
                r.id === requestId
                    ? { ...r, status: action === 'approve' ? 'approved' : 'rejected' }
                    : r
            ));
            toast({
                title: `Request ${action}d`,
                description: `User access has been ${action}d.`
            });
        } catch (error) {
            console.error(`Failed to ${action} request:`, error);
            toast({
                title: 'Action failed',
                description: `Failed to ${action} access request.`,
                variant: 'destructive'
            });
        }
    };

    const formatDate = (ts) => {
        return new Date(ts * 1000).toLocaleString();
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center">
                        <Shield className="h-5 w-5 mr-2 text-teal-600" />
                        Access Management: {file?.name}
                    </DialogTitle>
                </DialogHeader>

                <div className="py-4">
                    {loading ? (
                        <div className="text-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-2"></div>
                            <p className="text-sm text-gray-500">Loading requests...</p>
                        </div>
                    ) : requests.length === 0 ? (
                        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                            <Shield className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                            <h3 className="text-sm font-medium text-gray-900">No Access Requests</h3>
                            <p className="text-xs text-gray-500 mt-1">When users request access to this file, they will appear here.</p>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>User</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Requested</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {requests.map((req) => (
                                    <TableRow key={req.id}>
                                        <TableCell>
                                            <div className="flex items-center">
                                                <img src={req.requester_avatar} alt="" className="h-6 w-6 rounded-full mr-2" />
                                                <span className="text-sm font-medium">
                                                    {req.requester_name || (req.requester_username ? `@${req.requester_username}` : 'Unknown User')}
                                                </span>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant={
                                                req.status === 'approved' ? 'success' :
                                                    req.status === 'rejected' ? 'destructive' : 'secondary'
                                            }>
                                                {req.status}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-xs text-gray-500">
                                            {formatDate(req.requested_at)}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {req.status === 'pending' && (
                                                <div className="flex justify-end space-x-2">
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                                                        onClick={() => handleProcess(req.id, 'approve')}
                                                    >
                                                        <UserCheck className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                                        onClick={() => handleProcess(req.id, 'deny')}
                                                    >
                                                        <UserX className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </div>

                <div className="flex justify-end">
                    <Button onClick={onClose} variant="outline">Close</Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default FileAccessManager;
